import axios from 'axios';
import { toast } from 'react-toastify';
import { logger } from "../utils/logger";
import { API_URL as ROOT_API_URL } from './api';

// Add API key for tax registration endpoints
const API_KEY = process.env.REACT_APP_API_KEY || 'test-key-123';
const API_HEADERS = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };
// Align tax service with the same base host as the shared API client to avoid proxy 504s in dev
const API_URL = `${ROOT_API_URL}/api/v1`;

// Create API instance with auth token
const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: API_HEADERS  // Add default headers including API key
});

// Add request interceptor for auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Add the API key to all requests
    config.headers['X-API-Key'] = API_KEY;
    
    // Log request for debugging
    logger.log('Tax API Request:', {
      url: config.url,
      method: config.method,
      headers: { ...config.headers, Authorization: token ? 'Bearer [REDACTED]' : undefined },
      data: config.data
    });
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging and error mapping
apiClient.interceptors.response.use(
  (response) => {
    // logger.log('Tax API Response:', response.status, response.data);
    return response;
  },
  (error) => {
    // Log error for debugging (without exposing to user)
    logger.error('Tax API Error:', error.response ? error.response.status : 'No response', 
                 error.response ? error.response.data : error.message);
    
    // The error will be caught by the calling code and mapped to user-friendly message
    // We don't transform it here to preserve the original error structure
    return Promise.reject(error);
  }
);

// Alias for backwards-compatibility – anywhere that previously used `api.*` will now use the same axios instance
const api = apiClient;

// Fallback tax code categories when API is unavailable
const FALLBACK_CATEGORIES = [
  { name: "Physical goods", count: 300 },
  { name: "Digital products", count: 150 },
  { name: "Services", count: 187 }
];

// Fallback tax codes for different categories
const FALLBACK_TAX_CODES = {
  "Physical goods": [
    { id: "txcd_99999999", name: "General - Tangible Goods", description: "A physical good that can be moved or touched. Also known as tangible personal property.", category_type: "Physical goods" },
    { id: "txcd_00000000", name: "Nontaxable", description: "Any nontaxable good or service which can be used to ensure no tax is applied, even for jurisdictions that impose a tax.", category_type: "Physical goods" },
    { id: "txcd_40010001", name: "Books - Non-Textbooks", description: "Books that are not textbooks, workbooks, or other educational materials.", category_type: "Physical goods" },
    { id: "txcd_40020001", name: "Clothing - General", description: "Clothing and footwear for everyday use.", category_type: "Physical goods" },
    { id: "txcd_40030001", name: "Computer Hardware", description: "Computer equipment including desktops, laptops, tablets, and components.", category_type: "Physical goods" },
    { id: "txcd_41030001", name: "Food & Groceries - General", description: "Food products intended for human consumption.", category_type: "Physical goods" },
    { id: "txcd_43010001", name: "Medical Devices", description: "Medical equipment and devices used for patient treatment or monitoring.", category_type: "Physical goods" },
    { id: "txcd_47010001", name: "Office Supplies", description: "Office supplies used for business or home office purposes.", category_type: "Physical goods" },
    { id: "txcd_49010001", name: "Sporting Goods", description: "Equipment and accessories for sports and recreational activities.", category_type: "Physical goods" },
    { id: "txcd_50010001", name: "Toys & Games", description: "Products designed for play, including toys, board games, and puzzles.", category_type: "Physical goods" }
  ],
  "Digital products": [
    { id: "txcd_10000000", name: "General - Electronically Supplied Services", description: "A digital service provided mainly through the internet with minimal human involvement, relying on information technology.", category_type: "Digital products" },
    { id: "txcd_10010001", name: "Infrastructure as a service (IaaS) - personal use", description: "Cloud service offering infrastructure resources over the internet for personal use.", category_type: "Digital products" },
    { id: "txcd_10101000", name: "Infrastructure as a service (IaaS) - business use", description: "Cloud service offering infrastructure resources over the internet for business use.", category_type: "Digital products" },
    { id: "txcd_10103000", name: "Software as a service (SaaS) - personal use", description: "Cloud services software delivered over the internet for personal use.", category_type: "Digital products" },
    { id: "txcd_10103001", name: "Software as a service (SaaS) - business use", description: "Cloud services software delivered over the internet for business use.", category_type: "Digital products" },
    { id: "txcd_10201000", name: "Video Games - downloaded - non subscription", description: "Video or electronic games that are transferred electronically with permanent access.", category_type: "Digital products" },
    { id: "txcd_10301000", name: "Digital Media - Movies/TV - non subscription", description: "Digital video content with permanent rights to access and view.", category_type: "Digital products" },
    { id: "txcd_10401000", name: "Digital Media - Music - non subscription", description: "Digital audio content with permanent rights to access and listen.", category_type: "Digital products" },
    { id: "txcd_10501001", name: "Digital Books", description: "Books in digital format, including e-books and digital publications.", category_type: "Digital products" },
    { id: "txcd_10601001", name: "Website Creation and Maintenance", description: "Services for creating, hosting, and maintaining websites.", category_type: "Digital products" }
  ],
  "Services": [
    { id: "txcd_20030000", name: "General - Services", description: "General category for services when there is no more specific services category.", category_type: "Services" },
    { id: "txcd_20010001", name: "Accounting and Bookkeeping Services", description: "Professional services related to accounting, bookkeeping, and financial record management.", category_type: "Services" },
    { id: "txcd_20020001", name: "Advertising Services", description: "Services for promoting products, services, or businesses to potential customers.", category_type: "Services" },
    { id: "txcd_20030001", name: "Consulting Services", description: "Professional advice and guidance provided to businesses or individuals.", category_type: "Services" },
    { id: "txcd_20040001", name: "Educational Services", description: "Services related to teaching, training, and knowledge transfer.", category_type: "Services" },
    { id: "txcd_20050001", name: "Legal Services", description: "Professional services provided by attorneys and legal professionals.", category_type: "Services" },
    { id: "txcd_50021001", name: "Fitness Centers - Dues and Membership Fees", description: "Charges associated with recurring membership dues for health and fitness clubs.", category_type: "Services" },
    { id: "txcd_20060001", name: "Real Estate Services", description: "Services related to property management, sales, and leasing.", category_type: "Services" },
    { id: "txcd_20070001", name: "Repair and Maintenance Services", description: "Services for fixing, maintaining, or servicing goods and property.", category_type: "Services" },
    { id: "txcd_20080001", name: "Transportation Services", description: "Services for moving people or goods from one location to another.", category_type: "Services" }
  ]
};

const taxService = {
  // Calculate tax for a transaction
  calculateTax: async (orderData) => {
    try {
      logger.log('Original order data received:', orderData);
      
      // Ensure all line items have required fields and valid values
      const validatedLineItems = Array.isArray(orderData.line_items) ? 
        orderData.line_items.map((item, index) => {
          if (!item || typeof item !== 'object') {
            logger.error(`Line item ${index} is not a valid object:`, item);
            // Create a valid item object
            return {
              amount: 100, // Default 1 dollar in cents
              quantity: 1,
              tax_code: "txcd_99999999",
              reference: "Default Product",
              tax_behavior: "exclusive"
            };
          }
          
          // Ensure amount is a positive integer
          let amount;
          if (typeof item.amount === 'number') {
            amount = Math.max(1, Math.round(item.amount));
          } else if (typeof item.amount === 'string' && !isNaN(parseInt(item.amount))) {
            amount = Math.max(1, Math.round(parseInt(item.amount)));
          } else {
            amount = 100; // Default to $1 if invalid
          }
          
          // Log amount conversion for debugging
          logger.log(`Line item ${index} amount conversion:`, {
            original: item.amount,
            converted: amount,
            dollars: amount / 100
          });
          
          // Validate amount isn't too large for the engine
          if (amount > 99999999 * 100) { // $99.9M in cents
            logger.warn(`Line item ${index} amount ${amount} cents (${amount/100} dollars) is very large`);
          }
          
          // Ensure quantity is an integer
          let quantity;
          if (typeof item.quantity === 'number') {
            quantity = Math.max(1, Math.round(item.quantity));
          } else if (typeof item.quantity === 'string' && !isNaN(parseInt(item.quantity))) {
            quantity = Math.max(1, Math.round(parseInt(item.quantity)));
          } else {
            quantity = 1; // Default to 1 if invalid
          }
          
          // Create a new plain object with all required fields
          return {
            amount: amount,
            quantity: quantity,
            tax_code: item.tax_code || "txcd_99999999", // Default general tax code
            reference: item.reference || "Product",
            tax_behavior: "exclusive" // Add explicit tax behavior
          };
        }) : [];
      
      // If no line items, create a default one
      if (validatedLineItems.length === 0 && orderData.amount) {
        const amount = typeof orderData.amount === 'number' ? 
          Math.max(1, Math.round(orderData.amount * 100)) : 100;
        
        validatedLineItems.push({
          amount: amount, // Minimum 1 cent
          quantity: 1,
          tax_code: "txcd_99999999", // General - Tangible Goods
          reference: "Product",
          tax_behavior: "exclusive" // Add explicit tax behavior
        });
      }
      
      // Ensure we have at least one line item
      if (validatedLineItems.length === 0) {
        validatedLineItems.push({
          amount: 100, // Default 1 dollar in cents
          quantity: 1,
          tax_code: "txcd_99999999",
          reference: "Default Product",
          tax_behavior: "exclusive"
        });
      }
      
      // Build the engine request
      const engineRequest = {
        currency: (orderData.currency?.toLowerCase() || 'usd'),
        customer_details: {
          address: {
            line1: orderData.customer_details?.address?.line1 || '',
            city: orderData.customer_details?.address?.city || '',
            state: orderData.customer_details?.address?.state || '',
            postal_code: orderData.customer_details?.address?.postal_code || '',
            country: orderData.customer_details?.address?.country || 'US'
          },
          address_source: orderData.customer_details?.address_source || "shipping",
          ip_address: null // Don't use IP address for detection
        },
        line_items: validatedLineItems
      };

      // Add shipping cost if provided
      if (orderData.shipping_cost?.amount || (orderData.shipping_amount && orderData.shipping_amount > 0)) {
        let shippingAmount;
        
        if (typeof orderData.shipping_cost?.amount === 'number') {
          shippingAmount = Math.max(1, Math.round(orderData.shipping_cost.amount * 100));
        } else if (orderData.shipping_amount) {
          shippingAmount = Math.max(1, Math.round(orderData.shipping_amount * 100));
        } else {
          shippingAmount = 100; // Default to $1
        }
        
        engineRequest.shipping_cost = {
          amount: shippingAmount, // Convert to cents
          tax_code: "txcd_92010001", // Shipping tax code
          tax_behavior: "exclusive"
        };
      }
      
      logger.log('Final tax calculation request to API:', JSON.stringify(engineRequest, null, 2));
      
      // Make API call to tax calculation endpoint using api
      const response = await api.post(`/tax/engine/calculate-test`, engineRequest, {
        timeout: 20000, // Increase timeout for tax calculations
        headers: {
          'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
        }
      });
      
      logger.log('Raw tax calculation response from API:', response.data);
      
      // Transform the engine response to our application format
      const lineItems = response.data.line_items?.data || [];
      
      // Calculate subtotal from actual line items in the response
      const subtotalCents = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      const subtotal = subtotalCents / 100;
      
      const taxAmount = (response.data.tax_amount_exclusive || 0) / 100; // Convert from cents
      const total = (response.data.amount_total || 0) / 100; // Convert from cents
      
      // Calculate effective rate based on actual subtotal
      const effectiveRate = subtotal > 0 ? parseFloat((taxAmount / subtotal).toFixed(4)) : 0;
      
      // Log calculation details for debugging
      logger.log('Tax calculation breakdown:', {
        subtotalCents,
        subtotal,
        taxAmountCents: response.data.tax_amount_exclusive,
        taxAmount,
        totalCents: response.data.amount_total,
        total,
        effectiveRate,
        expectedTaxRate: response.data.tax_breakdown?.[0]?.tax_rate_details?.percentage_decimal
      });
      
      const taxResult = {
        subtotal: subtotal,
        taxAmount: taxAmount,
        total: total,
        effectiveRate: effectiveRate,
        shipping: orderData.shipping_amount || 0,
        handling: orderData.handling_amount || 0,
        jurisdictions: (response.data.tax_breakdown || []).map(tax => ({
          name: tax.tax_rate_details.state || tax.tax_rate_details.country,
          type: tax.tax_rate_details.tax_type,
          rate: parseFloat(tax.tax_rate_details.percentage_decimal) / 100,
          taxAmount: tax.amount / 100, // Convert from cents
          taxabilityReason: tax.taxability_reason // Add reason for debugging
        })),
        engine: 'gotrutax',
        calculationRef: response.data.id,
        lineItems: lineItems.map(item => ({
          id: item.id,
          amount: item.amount / 100,
          amount_tax: item.amount_tax / 100,
          quantity: item.quantity,
          reference: item.reference,
          tax_code: item.tax_code,
          tax_behavior: item.tax_behavior
        }))
      };
      
      logger.log('Final transformed tax result:', taxResult);
      
      return taxResult;
    } catch (error) {
      logger.error('Error calculating tax:', error);
      
      // Check if it's an API key error
      if (error.response?.data?.includes && error.response.data.includes('Invalid API Key provided')) {
        throw new Error('The tax engine is temporarily unavailable. Please try again shortly.');
      }
      
      // Check if it's a validation error
      if (error.response?.status === 422) {
        throw new Error(`Validation error: ${error.response.data.detail || 'Missing required fields'}`);
      }
      
      // Check for invalid object error
      if (error.response?.data?.includes && error.response.data.includes('Invalid object')) {
        throw new Error('Invalid request format. Please check line items and ensure all required fields are provided.');
      }
      
      // Handle other errors
      throw new Error(error.response?.data?.detail || error.message || 'Error calculating tax');
    }
  },

  // Register for sales tax in a jurisdiction
  registerForSalesTax: async (registrationData) => {
    try {
      // Make sure registrationData has all required fields
      if (!registrationData.country) {
        throw new Error("Country is required for tax registration");
      }
      
      if (registrationData.country === 'US' && !registrationData.state) {
        throw new Error("State is required for US tax registrations");
      }
      
      if (!registrationData.registration_type) {
        throw new Error("Registration type is required");
      }
      
      if (!registrationData.start_date) {
        throw new Error("Start date is required");
      }
      
      // Backend route is /api/v1/tax-registrations/ (there is no /registrations; it 404'd).
      const response = await api.post('/tax-registrations/', registrationData);
      return response.data;
    } catch (error) {
      logger.error('Error registering for sales tax:', error);
      throw error;
    }
  },

  // List tax registrations
  listSalesTaxRegistrations: async (params = {}) => {
    try {
      // Backend route is /api/v1/tax-registrations/ (GET /registrations 404'd on every company page).
      const response = await api.get('/tax-registrations/', { params });
      return response.data;
    } catch (error) {
      logger.error('Error listing registrations:', error);
      throw error;
    }
  },

  // Set head office address
  setHeadOfficeAddress: async (addressData) => {
    try {
      const response = await api.post(`/head-office`, addressData);
      return response.data;
    } catch (error) {
      logger.error('Error setting head office address:', error);
      throw error;
    }
  },

  // Get tax nexus status
  getNexusStatus: async (companyId) => {
    try {
      const response = await api.get(`/nexus-status?company_id=${companyId}`);
      return response.data;
    } catch (error) {
      logger.error('Error getting nexus status:', error);
      throw error;
    }
  },

  // Get tax thresholds
  getTaxThresholds: async () => {
    try {
      const response = await api.get(`/thresholds`);
      return response.data;
    } catch (error) {
      logger.error('Error getting tax thresholds:', error);
      throw error;
    }
  },

  /**
   * Get tax codes
   * @param {Object} params - Optional query parameters
   * @returns {Promise<Array>} - Array of tax codes
   */
  getTaxCodes: async (params = {}) => {
    // Ensure we always request enough rows to include the full catalogue (currently ~637 codes)
    const mergedParams = { limit: 1000, ...params };
    try {
      logger.log('Getting tax codes with params:', mergedParams);
      
      // Add the API key directly to headers for this request
      // Backend route lives under /api/v1/tax/tax-codes
      const response = await api.get('/tax/tax-codes', { 
        params: mergedParams,
        headers: {
          'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
        }
      });
      
      // logger.log('Tax codes response:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error fetching tax codes:', error);
      
      // Fallback to predefined tax codes when API is unavailable
      if (mergedParams.category_type) {
        return FALLBACK_TAX_CODES[mergedParams.category_type] || [];
      }
      
      // If no category specified, return all fallback tax codes
      return [
        ...FALLBACK_TAX_CODES["Physical goods"],
        ...FALLBACK_TAX_CODES["Digital products"],
        ...FALLBACK_TAX_CODES["Services"]
      ];
    }
  },

  /**
   * Get tax code categories
   * @returns {Promise<Array>} - Array of category names
   */
  getTaxCodeCategories: async () => {
    try {
      // Add the API key directly to headers for this request
      // Backend route lives under /api/v1/tax/tax-codes/categories
      const response = await api.get('/tax/tax-codes/categories', {
        headers: {
          'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
        }
      });
      return response.data;
    } catch (error) {
      logger.error('Error fetching tax code categories:', error);
      
      // Return fallback categories when API is unavailable
      return FALLBACK_CATEGORIES;
    }
  },

  /**
   * Sync the product tax code catalogue
   * @returns {Promise<Object>} - Result of sync operation
   */
  syncTaxCodes: async () => {
    try {
      // Backend route lives under /api/v1/tax/sync-tax-codes
      // The api client adds the bearer token and X-API-Key. The server answers within ~20 s; a longer
      // refresh continues in the background ({ in_progress: true }).
      const response = await api.post('/tax/sync-tax-codes', null, { timeout: 45000 });
      return response.data;
    } catch (error) {
      logger.error('Error syncing tax codes:', error);
      throw error;
    }
  },

  // Set company tax code
  setCompanyTaxCode: async (companyId, taxCode) => {
    try {
      logger.log(`Setting company tax code: companyId=${companyId}, taxCode=${taxCode}`);
      
      // Ensure companyId is a number
      const numericCompanyId = parseInt(companyId);
      if (isNaN(numericCompanyId)) {
        throw new Error(`Invalid company ID: ${companyId}`);
      }
      
      // Ensure tax code is a string
      const taxCodeStr = String(taxCode || "");
      
      const payload = {
        company_id: numericCompanyId,
        tax_code: taxCodeStr
      };
      
      logger.log('Tax code update request payload:', payload);
      // There is no /company-tax-code route; the company update endpoint accepts tax_code.
      const response = await api.put(`/companies/${numericCompanyId}`, {
        tax_code: taxCodeStr
      });
      logger.log('Tax code update response:', response.data);
      
      toast.success('Tax code updated successfully');
      return response.data;
    } catch (error) {
      logger.error('Error setting company tax code:', error);
      
      // Provide more context for debugging
      if (error.response) {
        logger.error('Server response:', error.response.status, error.response.data);
      }
      
      toast.error('Failed to update tax code');
      throw error;
    }
  },
  
  // Alias for setCompanyTaxCode used by CompanyTaxCode component
  updateCompanyTaxCode: async (companyId, taxCode) => {
    return taxService.setCompanyTaxCode(companyId, taxCode);
  },

  // Create a tax registration
  createTaxRegistration: async (data) => {
    try {
      const response = await api.post('/tax-registrations/', data, {
        headers: {
          'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
        }
      });
      return response.data;
    } catch (error) {
      logger.error('Error creating tax registration:', error);
      throw error;
    }
  },

  /**
   * Get tax registrations
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (active, expired, scheduled)
   * @param {number} params.company_id - Filter by company ID
   * @param {string} params.tax_account_id - Filter by tax account ID
   * @returns {Promise<Array>} - List of registrations
   */
  getTaxRegistrations: async (params = {}) => {
    try {
      const response = await api.get('/tax-registrations/', { 
        params,
        headers: {
          'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
        }
      });
      return response.data;
    } catch (error) {
      logger.error('Error getting tax registrations:', error);
      throw error;
    }
  },
  
  /**
   * End a tax registration
   * @param {string} id - Registration ID to end
   * @param {boolean} immediate - Whether to end the registration immediately (true) or at the end of the current period (false)
   * @returns {Promise<Object>} - The updated registration
   */
  endSalesTaxRegistration: async (id, immediate = false) => {
    if (!id) {
      throw new Error('Registration ID is required');
    }

    try {
      const response = await api.post(`/tax-registrations/${id}/end`, { 
        immediate: !!immediate 
      }, {
        headers: {
          'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
        }
      });
      return response.data;
    } catch (error) {
      logger.error(`Error ending tax registration ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get tax calculation history
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Tax calculation history
   */
  getTaxCalculationHistory: async (params = {}) => {
    try {
      const response = await api.get('/tax-calculations', { params });
      return response.data;
    } catch (error) {
      logger.error('Error fetching tax calculation history:', error);
      throw error;
    }
  },

  /**
   * Get a specific tax calculation
   * @param {string|number} id - Tax calculation ID
   * @returns {Promise<Object>} - Tax calculation details
   */
  /**
   * Recorded sales tax filings for a company, newest due date first.
   * @param {number} companyId
   * @param {('upcoming'|'submitted'|'completed'|'attention')} [status]
   * @returns {Promise<Array>} filings (empty when none are recorded)
   */
  getTaxFilings: async (companyId, status) => {
    const params = { company_id: companyId };
    if (status) params.status = status;
    const response = await api.get('/tax/filings', { params });
    return Array.isArray(response.data) ? response.data : [];
  },

  getTaxCalculation: async (id) => {
    try {
      const response = await api.get(`/tax-calculations/${id}`);
      return response.data;
    } catch (error) {
      logger.error(`Error fetching tax calculation ${id}:`, error);
      throw error;
    }
  }
};

export const getTaxCodes = taxService.getTaxCodes;
export const getTaxCodeCategories = taxService.getTaxCodeCategories;

export default taxService;